import axios from "axios";
import auth from "./auth";
import { STORAGE_KEYS } from "./storageKeys";

const singleton = 'singleton';
const singletonEnforcer = 'singleton1';

class ApiService {
  constructor(enforcer) {
    if (enforcer !== singletonEnforcer) {
      throw new Error('Cannot construct singleton');
    }
    this.createInstance();
  }

  createInstance() {
    this.session = axios.create();

    this.session.interceptors.request.use(
      async (config) => {
        // Skip if already absolute — 401-retry re-enters the interceptor and would double-prepend.
        if (!/^https?:\/\//i.test(config.url)) {
          config.url = (config.baseURL || window.config.REACT_APP_APIGATEWAY) + config.url;
        }
        if (config.useVendorToken) {
          if (!config._skipVendorRefresh) {
            try {
              const { ensureVendorToken } = await import("./taskerAuth");
              await ensureVendorToken();
            } catch { /* fall through with whatever's in storage */ }
          }
          const vendorToken = sessionStorage.getItem(STORAGE_KEYS.VENDOR_TOKEN);
          if (vendorToken) {
            config.headers["Authorization"] = `Bearer ${vendorToken}`;
          }
          return config;
        }
        if (config.useReviewerToken) {
          const reviewerToken = sessionStorage.getItem(STORAGE_KEYS.REVIEWER_TOKEN);
          if (reviewerToken) {
            config.headers["Authorization"] = `Bearer ${reviewerToken}`;
          }
          return config;
        }
        let access_token = auth.getAccessToken(), user = auth.userDetails;
        if (access_token) {
          const tarsToken = user.tarsAccessToken || sessionStorage.getItem("TARS_ACCESS_TOKEN");
          config.headers["Authorization"] = config.useTarsToken
            ? `Bearer ${tarsToken}`
            : `Bearer ${access_token}`;
          if (config.data && typeof config.data === 'string') {
            let requestData = JSON.parse(config.data);
            requestData = {
              ...requestData,
              userid: user.email,
              access_token: tarsToken,
              emp: {
                "empid": user.email,
                "empname": user.email
              }
            }
            config.data = requestData;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    this.session.interceptors.response.use(
      async (response) => {
        return response.data;
      },
      async (error) => {
        console.log(error);
        const originalRequest = error.config;
        const status = error.response?.status;
        if (status === 401 && !originalRequest._retry) {
          if (originalRequest.skipAuthRefreshOn401) {
            return Promise.reject(error);
          }
          const isReviewerRoute = window.location.pathname.startsWith(
            "/vendor-onboarding/review",
          );
          if (originalRequest.useVendorToken) {
            if (originalRequest.skipVendor401Redirect || isReviewerRoute) {
              return Promise.reject(error);
            }
            // Set _retry before the awaited refresh so a re-entry on the same
            // request would short-circuit at the top-of-handler guard.
            originalRequest._retry = true;
            try {
              const { refreshVendorToken } = await import("./taskerAuth");
              const fresh = await refreshVendorToken();
              if (fresh) {
                originalRequest._skipVendorRefresh = true;
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers["Authorization"] = `Bearer ${fresh}`;
                return this.session(originalRequest);
              }
            } catch { /* refresh failed; fall through to logout */ }
            sessionStorage.removeItem(STORAGE_KEYS.VENDOR_TOKEN);
            sessionStorage.removeItem(STORAGE_KEYS.VENDOR_MOBILE);
            const alreadyOnLogin = window.location.pathname.startsWith(
              "/vendor-onboarding/login",
            );
            if (!alreadyOnLogin) {
              const params = new URLSearchParams(window.location.search);
              const taskId = params.get("task_id") || params.get("taskId");
              const q = taskId
                ? `?task_id=${encodeURIComponent(taskId)}`
                : "";
              window.location.assign(`/vendor-onboarding/login${q}`);
            }
            return Promise.reject(error);
          }
          if (originalRequest.useReviewerToken) {
            // Set _retry before the awaited exchange so a re-entry on the same
            // request would short-circuit at the top-of-handler guard.
            originalRequest._retry = true;
            sessionStorage.removeItem(STORAGE_KEYS.REVIEWER_TOKEN);
            sessionStorage.removeItem(STORAGE_KEYS.REVIEWER_TOKEN_EXPIRES);
            try {
              const { ensureReviewerToken } = await import("./taskerAuth");
              const freshToken = await ensureReviewerToken();
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["Authorization"] = `Bearer ${freshToken}`;
              return this.session(originalRequest);
            } catch (exchangeErr) {
              return Promise.reject(exchangeErr);
            }
          }
          originalRequest._retry = true;
          await auth.getRefreshedAccessToken();
          return this.session(originalRequest);
        }

        if (status === 403) {
          const data = error.response?.data;
          const message = (typeof data === 'string' && data) || data?.error || data?.message || 'not authorised';
          throw {status: 403, message};
        }

        return Promise.reject(error);
      },
    );
  }

  static get instance() {
    if (!this[singleton]) {
      this[singleton] = new ApiService(singletonEnforcer);
    }
    return this[singleton];
  }

  exec = (...params) => this.session(...params)
  get = (...params) => this.session.get(...params);
  post = (...params) => this.session.post(...params);
  put = (...params) => this.session.put(...params);
  patch = (...params) => this.session.patch(...params);
  remove = (...params) => this.session.delete(...params);
}

export default ApiService.instance;